import { useState } from "react";
import { Check, X, Lock, ShieldCheck, Zap, Star } from "lucide-react";
import { SITE } from "@/config/site";
import { Reveal, Kicker, SectionTitle } from "./motion";
import { CtaButton } from "./CtaButton";
import { UpsellModal } from "./UpsellModal";

const BASIC = [["+25.000 materiais pedagógicos", 1], ["Licença PLR para revenda", 1], ["Acesso vitalício e imediato", 1], ["Atualizações do acervo", 0], ["Kits prontos para revenda", 0], ["3 bônus exclusivos (R$ 111)", 0], ["Suporte via WhatsApp", 0]];
const PREMIUM = ["+25.000 materiais pedagógicos", "Licença PLR para revenda", "Kits prontos para revenda", "Acesso vitalício e imediato", "Atualizações do acervo", "Suporte via WhatsApp"];
const BONUS = ["Pack Canva Educacional Editável", "Guia Completo para Revender PLR", "Pack Artes para Divulgação"];

const Trust = ({ dark }) => (
  <div className={`mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs font-semibold ${dark ? "text-zinc-300" : "text-ink/60"}`}>
    <span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5" />Compra Segura</span>
    <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />Satisfação Garantida</span>
    <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5" />Acesso Imediato</span>
  </div>
);

const Price = ({ p, dark }) => (
  <>
    <p className={`mt-4 text-sm ${dark ? "text-zinc-300" : "text-ink/60"}`}>de <s>{p.old}</s> por</p>
    <p className="font-display text-7xl font-extrabold leading-none tracking-tight">R${p.price}<span className="text-3xl">{p.cents}</span></p>
    <p className={`mt-1 text-sm ${dark ? "text-zinc-300" : "text-ink/60"}`}>pagamento único</p>
  </>
);

const BasicPlan = ({ onBuy }) => (
  <div className="flex h-full flex-col rounded-[32px] bg-white p-8 ring-1 ring-ink/10 sm:p-10" data-testid="plan-basic">
    <h3 className="font-display text-2xl font-extrabold text-ink">Plano Básico</h3>
    <Price p={SITE.basic} />
    <ul className="mt-7 flex-1 space-y-3">
      {BASIC.map(([t, ok]) => (
        <li key={t} className={`flex items-start gap-3 text-sm sm:text-base ${ok ? "text-ink" : "text-ink/45 line-through"}`}>
          {ok ? <Check className="mt-0.5 h-5 w-5 shrink-0 text-mint" /> : <X className="mt-0.5 h-5 w-5 shrink-0 text-ink/35" />}{t}
        </li>
      ))}
    </ul>
    <CtaButton variant="ghost" onClick={onBuy} testId="plan-basic-button" className="mt-8 w-full !text-sm sm:!text-base">Quero o Básico por {SITE.basic.label}</CtaButton>
    <Trust />
  </div>
);

const PremiumPlan = () => (
  <div className="premium-ring relative h-full rounded-[34px] p-[2px]" data-testid="plan-premium">
    <div className="flex h-full flex-col rounded-[32px] bg-night p-8 text-white sm:p-10">
      <span className="absolute -top-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-sun px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-ink">
        <Star className="h-3.5 w-3.5 fill-ink" /> Mais escolhido
      </span>
      <h3 className="font-display text-2xl font-extrabold">Plano Premium</h3>
      <Price p={SITE.premium} dark />
      <span className="mt-3 inline-flex w-fit rounded-full bg-mint/20 px-3 py-1 text-xs font-bold text-emerald-300">{SITE.premium.save}</span>
      <ul className="mt-7 space-y-3">
        {PREMIUM.map((t) => <li key={t} className="flex items-start gap-3 text-sm sm:text-base"><Check className="mt-0.5 h-5 w-5 shrink-0 text-sun" />{t}</li>)}
      </ul>
      <div className="mt-6 flex-1 rounded-2xl bg-white/[0.06] p-5">
        <p className="text-sm font-bold">+ 3 bônus (<s className="text-zinc-400">R$ 111</s> grátis):</p>
        {BONUS.map((b) => <p key={b} className="mt-2 text-sm text-zinc-200"><span className="mr-2 rounded bg-rose px-1.5 py-0.5 text-[11px] font-bold">BÔNUS</span>{b}</p>)}
      </div>
      <CtaButton href={SITE.checkout.premium} testId="plan-premium-button" className="mt-8 w-full !text-sm sm:!text-base">Quero o Premium + 3 bônus</CtaButton>
      <Trust dark />
    </div>
  </div>
);

export const Pricing = () => {
  const [open, setOpen] = useState(false);
  return (
    <section id="planos" className="bg-lilac py-20 sm:py-28" data-testid="pricing-section">
      <div className="mx-auto max-w-5xl px-5">
        <Reveal className="text-center">
          <Kicker>Escolha seu plano</Kicker>
          <SectionTitle className="mx-auto mt-5 max-w-3xl text-ink">Comece sua <span className="hl-mark">renda extra</span> ainda hoje</SectionTitle>
          <p className="mx-auto mt-4 max-w-xl text-base text-ink/70 sm:text-lg">Pagamento único, sem mensalidade. Acesso imediato no seu e-mail.</p>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-2 md:items-stretch">
          <Reveal className="order-2 md:order-1"><BasicPlan onBuy={() => setOpen(true)} /></Reveal>
          <Reveal className="order-1 md:order-2" delay={0.1}><PremiumPlan /></Reveal>
        </div>
      </div>
      <UpsellModal open={open} onOpenChange={setOpen} />
    </section>
  );
};
